import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import Pill from '../../components/common/Pill';
import Modal from '../../components/common/Modal';
import ConfirmarModal from '../../components/common/ConfirmarModal';
import usePaginacao from '../../hooks/usePaginacao';
import * as quarentenaApi from '../../api/quarentena';
import { formatDataHora, tempoRelativo } from '../../utils/date';
import { formatBytes, formatJson } from '../../utils/formato';

export default function Quarentena() {
  const navigate = useNavigate();
  const [chamadas, setChamadas] = useState(null);
  const [incluirIgnorados, setIncluirIgnorados] = useState(false);
  const [erro, setErro] = useState('');
  const [amostra, setAmostra] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [aviso, setAviso] = useState(null);

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await quarentenaApi.listar(incluirIgnorados);
      setChamadas(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar a quarentena.'));
    }
  }, [incluirIgnorados]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const { paginados, Paginacao } = usePaginacao(chamadas || [], 15);

  if (erro && !chamadas) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!chamadas) return <LoadingSpinner />;

  const promover = (chamada) =>
    setConfirmando({
      titulo: `Criar endpoint /hooks/${chamada.slug}`,
      mensagem:
        `O endpoint nasce ATIVO e SEM autenticação — é o modo certo para descobrir o formato do ` +
        `provedor. Configure a credencial depois de ver o primeiro evento chegar. As ` +
        `${chamada.tentativas} tentativa(s) já recusada(s) não viram eventos: elas foram ` +
        `respondidas com 404 e a amostra guardada aqui é truncada. O provedor precisa mandar de novo.`,
      textoBotao: 'Criar endpoint',
      tom: 'primary',
      acao: async () => {
        const { data } = await quarentenaApi.promover(chamada.id_chamada);
        await carregar();
        setAviso(`Endpoint "${data.nome}" criado. A próxima entrega do provedor já será registrada.`);
      },
    });

  return (
    <div>
      <PageHeader
        titulo="Chamadas não identificadas"
        subtitulo="Tentativas de entrega em slugs que não existem — o provedor recebeu 404"
        acoes={
          <>
            <label className="wh-btn wh-btn-outline" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={incluirIgnorados}
                onChange={(e) => setIncluirIgnorados(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Mostrar ignoradas
            </label>
            <button type="button" className="wh-btn wh-btn-outline" onClick={carregar}>
              <i className="bi bi-arrow-clockwise" /> Atualizar
            </button>
          </>
        }
      />

      {aviso && (
        <div className="alert alert-success d-flex justify-content-between align-items-center py-2 px-3" style={{ fontSize: 13 }}>
          <span>{aviso}</span>
          <button type="button" className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => navigate('/endpoints')}>
            Ver endpoints
          </button>
        </div>
      )}

      <div className="card-soft">
        {chamadas.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-shield-check" />
            <div style={{ fontWeight: 600, color: 'var(--wh-ink)' }}>Nada na quarentena.</div>
            <div style={{ fontSize: 13, marginTop: 4, maxWidth: 560, marginInline: 'auto' }}>
              Quando alguém chamar uma URL <code>/hooks/&lt;slug&gt;</code> que não existe, a tentativa aparece
              aqui — com quantas vezes tentou, de onde veio e o que estava mandando. É assim que um provedor
              configurado com a URL errada deixa de falhar em silêncio.
            </div>
          </div>
        ) : (
          <div className="wh-table-wrap">
            <table className="wh-table">
              <thead>
                <tr>
                  <th>Slug chamado</th>
                  <th>Origem</th>
                  <th style={{ textAlign: 'right' }}>Tentativas</th>
                  <th>Primeira</th>
                  <th>Última</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginados.map((c) => (
                  <tr key={c.id_chamada}>
                    <td>
                      <div className="wh-mono" style={{ fontWeight: 600 }}>/hooks/{c.slug}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--wh-muted)' }}>
                        {c.metodo} · {formatBytes(c.tamanho_bytes)}
                        {c.ignorado && (
                          <>
                            {' · '}
                            <Pill tone="neutral" icon="bi-eye-slash">
                              ignorada
                            </Pill>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="wh-mono">{c.ip_origem || '—'}</div>
                      <div
                        style={{ fontSize: 11.5, color: 'var(--wh-muted)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={c.user_agent}
                      >
                        {c.user_agent || 'sem user-agent'}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{c.tentativas}</td>
                    <td title={formatDataHora(c.primeira_em)} style={{ whiteSpace: 'nowrap' }}>
                      {tempoRelativo(c.primeira_em)}
                    </td>
                    <td title={formatDataHora(c.ultima_em)} style={{ whiteSpace: 'nowrap' }}>
                      {tempoRelativo(c.ultima_em)}
                    </td>
                    <td>
                      <div className="d-flex gap-1 justify-content-end">
                        <button
                          type="button"
                          className="wh-btn wh-btn-outline wh-btn-sm"
                          title="Ver o que foi enviado"
                          onClick={() => setAmostra(c)}
                        >
                          <i className="bi bi-eye" />
                        </button>
                        <button
                          type="button"
                          className="wh-btn wh-btn-primary wh-btn-sm"
                          title="Criar endpoint com este slug"
                          onClick={() => promover(c)}
                        >
                          <i className="bi bi-plus-lg" /> Criar endpoint
                        </button>
                        {!c.ignorado && (
                          <button
                            type="button"
                            className="wh-btn wh-btn-outline wh-btn-sm"
                            title="Sumir da lista, mas continuar contando as tentativas"
                            onClick={() =>
                              setConfirmando({
                                titulo: 'Ignorar estas chamadas',
                                mensagem: `As tentativas em /hooks/${c.slug} vindas de ${c.ip_origem || 'origem desconhecida'} somem da lista. A linha continua existindo e contando — é o que evita que quem insiste apareça de novo a cada tentativa.`,
                                textoBotao: 'Ignorar',
                                acao: () => quarentenaApi.ignorar(c.id_chamada).then(carregar),
                              })
                            }
                          >
                            <i className="bi bi-eye-slash" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="wh-btn wh-btn-danger wh-btn-sm"
                          title="Excluir da quarentena"
                          onClick={() =>
                            setConfirmando({
                              titulo: 'Excluir da quarentena',
                              mensagem: `A linha é apagada. Se o chamador insistir, ela reaparece do zero — o que é honesto, porque a tentativa continua acontecendo. Para parar de ver sem perder o contador, use "ignorar".`,
                              textoBotao: 'Excluir',
                              acao: () => quarentenaApi.excluir(c.id_chamada).then(carregar),
                            })
                          }
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Paginacao />

      {amostra && (
        <Modal titulo={`O que chegou em /hooks/${amostra.slug}`} onClose={() => setAmostra(null)} largura={720}>
          <div className="dado-linha">
            <div className="dado-linha__rotulo">Origem</div>
            <div className="dado-linha__valor wh-mono">{amostra.ip_origem || '—'}</div>
          </div>
          <div className="dado-linha">
            <div className="dado-linha__rotulo">User-Agent</div>
            <div className="dado-linha__valor">{amostra.user_agent || '—'}</div>
          </div>
          <div className="dado-linha">
            <div className="dado-linha__rotulo">Método e caminho</div>
            <div className="dado-linha__valor wh-mono">
              {amostra.metodo} {amostra.caminho}
            </div>
          </div>
          <div className="dado-linha">
            <div className="dado-linha__rotulo">Content-Type</div>
            <div className="dado-linha__valor wh-mono">{amostra.content_type || '—'}</div>
          </div>

          <h3 style={{ fontSize: 13, fontWeight: 700, marginTop: 18 }}>
            Corpo da primeira tentativa
            {amostra.corpo_truncado && (
              <span style={{ fontWeight: 400, color: 'var(--wh-muted)' }}> (truncado)</span>
            )}
          </h3>
          {amostra.corpo_amostra ? (
            <pre className="codigo-bloco" style={{ maxHeight: 260 }}>
              {amostra.corpo_amostra}
            </pre>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--wh-ink-2)' }}>
              Sem corpo (ou corpo binário, que não é guardado na quarentena).
            </div>
          )}

          <h3 style={{ fontSize: 13, fontWeight: 700, marginTop: 18 }}>
            Cabeçalhos
            {!amostra.segredos_visiveis && (
              <span style={{ fontWeight: 400, color: 'var(--wh-muted)' }}>
                {' '}
                — valores sensíveis ocultos para o seu perfil
              </span>
            )}
          </h3>
          <pre className="codigo-bloco" style={{ maxHeight: 220 }}>
            {formatJson(amostra.headers_amostra)}
          </pre>
        </Modal>
      )}

      {confirmando && (
        <ConfirmarModal
          titulo={confirmando.titulo}
          mensagem={confirmando.mensagem}
          textoBotao={confirmando.textoBotao}
          tom={confirmando.tom}
          onConfirmar={confirmando.acao}
          onClose={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
